import React, { useState, useEffect, useCallback } from 'react';
import { CTADialogProps } from '../../types/components';
import { MdChevronLeft, MdChevronRight, MdArrowForward, MdClose } from 'react-icons/md';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { Button } from './Button';
import { SafeImage } from './safe-image';

const CTADialog: React.FC<CTADialogProps> = ({
  isOpen,
  onClose,
  content,
  currentSlide = 0,
  initialSlide,
  errorMessage,
  featureContext, // Used for debugging/tracking purposes
  onSlideChange,
  className = '',
}) => {
  const [activeSlide, setActiveSlide] = useState(initialSlide ?? currentSlide);

  // Reset slide when content changes, but use initialSlide if provided
  useEffect(() => {
    setActiveSlide(initialSlide ?? 0);
  }, [content.type, initialSlide]);

  // Clean up body styles when dialog closes
  useEffect(() => {
    if (!isOpen) {
      // Remove pointer-events style from body when dialog closes
      document.body.style.pointerEvents = '';
      document.body.style.removeProperty('pointer-events');

      // Also remove any other potential blocking styles
      const bodyStyle = document.body.style;
      if (bodyStyle.pointerEvents === 'none') {
        bodyStyle.pointerEvents = '';
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.style.pointerEvents = '';
      document.body.style.removeProperty('pointer-events');
    };
  }, [isOpen]);

  // Handle slide change
  const handleSlideChange = useCallback(
    (slideIndex: number) => {
      setActiveSlide(slideIndex);
      onSlideChange?.(slideIndex);
    },
    [onSlideChange]
  );

  // Navigate to next slide
  const nextSlide = useCallback(() => {
    const nextIndex = (activeSlide + 1) % content.slides.length;
    handleSlideChange(nextIndex);
  }, [activeSlide, content.slides.length, handleSlideChange]);

  // Navigate to previous slide
  const prevSlide = useCallback(() => {
    const prevIndex = activeSlide === 0 ? content.slides.length - 1 : activeSlide - 1;
    handleSlideChange(prevIndex);
  }, [activeSlide, content.slides.length, handleSlideChange]);

  // Wrapped close handler to ensure cleanup
  const handleClose = useCallback(() => {
    onClose();

    // Add delay to allow dialog animation to complete before removing pointer events
    setTimeout(() => {
      document.body.style.pointerEvents = '';
      document.body.style.removeProperty('pointer-events');
    }, 200); // 200ms delay to match dialog animation duration
  }, [onClose]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          handleClose();
          break;
        case 'ArrowLeft':
          prevSlide();
          break;
        case 'ArrowRight':
          nextSlide();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, handleClose, nextSlide, prevSlide]);

  // Auto-advance slides (optional)
  useEffect(() => {
    if (!isOpen) return;

    const timer = setInterval(() => {
      nextSlide();
    }, 8000); // Change slide every 8 seconds

    return () => clearInterval(timer);
  }, [isOpen, nextSlide]);

  // Handle CTA button click
  const handleCTAClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    handleClose(); // Close dialog after CTA click
  };

  const currentSlideData = content.slides[activeSlide];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        hideClose
        className={`max-w-[calc(100vw-30px)] md:!max-w-4xl rounded-lg p-0 gap-0 overflow-hidden z-[1000] ${className}`}
        data-feature-context={featureContext}
      >
        {/* Custom Close Button */}
        <Button
          onClick={handleClose}
          variant="ghost"
          size="icon"
          className="absolute right-4 top-4 z-10 rounded-full !bg-white/80 hover:!bg-white shadow-md hover:shadow-lg"
          aria-label="Close dialog"
        >
          <MdClose className="w-5 h-5 !text-red-600" />
        </Button>

        <div className="flex flex-col md:!flex-row min-h-[500px] gap-4">
          {/* Image Section */}
          <div className="md:w-1/2 relative overflow-hidden">
            <div className="relative h-64 md:h-full">
              {content.slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`absolute flex items-center justify-center p-8 inset-0 transition-transform duration-500 ease-in-out ${
                    index === activeSlide
                      ? 'translate-x-0'
                      : index < activeSlide
                        ? '-translate-x-full'
                        : 'translate-x-full'
                  }`}
                >
                  <SafeImage
                    src={slide.imageUrl}
                    alt={slide.title}
                    className="w-full h-full object-contain object-center"
                    loading="lazy"
                    fallback={
                      <div className="w-full min-h-[300px] border bg-gray-300 my-auto p-10 flex items-center justify-center">
                        {slide.title}
                      </div>
                    }
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20" />
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            {content.slides.length > 1 && (
              <>
                <Button
                  onClick={prevSlide}
                  variant="secondary"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full !bg-white bg-opacity-80 hover:bg-opacity-100 shadow-lg"
                  aria-label="Previous slide"
                >
                  <MdChevronLeft className="w-5 h-5 text-gray-700" />
                </Button>
                <Button
                  onClick={nextSlide}
                  variant="secondary"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full !bg-white bg-opacity-80 hover:bg-opacity-100 shadow-lg"
                  aria-label="Next slide"
                >
                  <MdChevronRight className="w-5 h-5 text-gray-700" />
                </Button>
              </>
            )}
          </div>

          {/* Content Section */}
          <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center">
            <div className="space-y-6">
              {/* Slide Indicators */}
              {content.slides.length > 1 && (
                <div className="flex space-x-2 justify-center md:justify-start">
                  {content.slides.map((_, index) => (
                    <Button
                      key={index}
                      onClick={() => handleSlideChange(index)}
                      variant="ghost"
                      size="sm"
                      className={`w-3 h-3 p-0 rounded-full transition-all duration-200 ${
                        index === activeSlide
                          ? 'bg-blue-600 scale-110 hover:bg-blue-700'
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}

              {/* Error Message (if provided) */}
              {errorMessage && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <span className="text-red-500 text-lg">⚠️</span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">Feature Limited</p>
                      <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Slide Content */}
              <DialogHeader className="text-center md:text-left">
                <DialogTitle className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {currentSlideData.title}
                </DialogTitle>
                <DialogDescription className="text-lg text-gray-600 leading-relaxed mb-8">
                  {currentSlideData.description}
                </DialogDescription>
              </DialogHeader>

              {/* CTA Button */}
              <div className="flex justify-center md:justify-start">
                <Button
                  onClick={() => handleCTAClick(currentSlideData.ctaUrl)}
                  size="lg"
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  {currentSlideData.ctaText}
                  <MdArrowForward className="ml-2 w-5 h-5" />
                </Button>
              </div>

              {/* Skip Option */}
              <div className="flex justify-center md:justify-start">
                <Button
                  onClick={handleClose}
                  variant="ghost"
                  size="sm"
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Maybe later
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CTADialog;
